import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { ErrorPage } from "./ErrorPage";
import jsonFromDocument from "cerulean/src/JsonFromDocument";

const getErrorDetail = (error: unknown) => {
  const isDemo = jsonFromDocument("institution_is_demo");
  if (!isDemo || !(error instanceof Error)) return "";
  return error.message;
};

export function ErrorBoundary() {
  const error = useRouteError();
  const errorDetail = getErrorDetail(error);
  const isNotFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    if (!error || isNotFound) return;

    console.error("Staff route error boundary caught an error", error);

    if (isRouteErrorResponse(error)) {
      Sentry.captureException(new Error(`Route Error: ${error.status} ${error.statusText}`), {
        extra: {
          status: error.status,
          statusText: error.statusText,
        },
      });
      return;
    }

    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }

    Sentry.captureException(new Error("Unknown route error"), {
      extra: {
        error,
      },
    });
  }, [error, isNotFound]);

  if (isNotFound) {
    return <ErrorPage title="Page not found" text="The page you're looking for doesn't exist." />;
  }

  return (
    <ErrorPage
      title="We're sorry"
      text="An unexpected error has occurred. We apologize for the inconvenience."
      detail={errorDetail}
    />
  );
}
